import { collection, doc, deleteDoc, getDoc, getDocs, writeBatch } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { getAuth, deleteUser } from "firebase/auth";
import { db, storage } from "../firebase/config";
import { userService } from "./userService";
import { usernameService } from "./usernameService";
import { notificationService } from "./notificationService";
import { getFollowingList, unfollowUser } from "./connections";
import { getUserPosts, deletePost } from "./postService";
import { organisationService } from "./organisationService";

// Subcollections that hold nothing but the user's own log/post/session docs —
// safe to wipe wholesale rather than routing through per-item delete calls.
const OWNED_SUBCOLLECTIONS = [
  "trainingLogs",
  "recoveryLogs",
  "negativeInfluencesLogs",
  "niggleLogs",
  "performanceLogs",
  "healthLogs",
  "stepLogs",
  "noteLogs",
  "femaleCycleLogs",
  "wellnessLogs",
  "peakScoreLogs",
  "activityLogs",
];

const BATCH_SIZE = 400;

async function wipeCollection(path: string): Promise<void> {
  const snap = await getDocs(collection(db, path));
  const docs = snap.docs;
  for (let i = 0; i < docs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    docs.slice(i, i + BATCH_SIZE).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
}

async function deleteStorageObjectIfExists(path: string): Promise<void> {
  try {
    await deleteObject(ref(storage, path));
  } catch (error: any) {
    if (error?.code !== "storage/object-not-found") {
      console.error(`Error deleting storage object ${path}:`, error);
    }
  }
}

/**
 * The self-service deletion the user performs from /complete-account-removal
 * once their request is 28 days past confirmation. Runs entirely on the
 * client under the caller's own auth — no backend function deletes user
 * data, so a bug here can only ever affect the signed-in user's own account.
 *
 * Order matters: everything that still needs the user's Firestore/Storage
 * permissions must run before deleteUser(), and the reminder lookup needs
 * the users/{uid} doc to still exist, so that goes first.
 */
export async function completeAccountRemoval(uid: string): Promise<void> {
  const userData = await userService.getUserData(uid);

  // Notifications — reminder lookup reads users/{uid}.reminderGmtHour, so
  // this has to happen before the user doc is deleted below.
  await notificationService.deleteReminder(uid);
  await wipeCollection(`notifications/${uid}/notifications`);
  await deleteDoc(doc(db, "notifications", uid)).catch(() => undefined);

  // Following / followers
  const following = await getFollowingList(uid);
  for (const followedUid of following) {
    await unfollowUser(uid, followedUid);
  }
  await deleteDoc(doc(db, "followers", uid)).catch(() => undefined);

  // Social posts
  const posts = await getUserPosts(uid);
  for (const post of posts) {
    await deletePost(uid, post.id);
  }

  // Day posts, logs, sessions
  await wipeCollection(`dayPosts/${uid}/posts`);
  await wipeCollection(`sessions/${uid}/sessions`);
  for (const collectionName of OWNED_SUBCOLLECTIONS) {
    await wipeCollection(`${collectionName}/${uid}/logs`);
  }

  // AI wizard conversations and insights
  const conversationsSnap = await getDocs(collection(db, `wizardConversations/${uid}/conversations`));
  for (const convDoc of conversationsSnap.docs) {
    await wipeCollection(`wizardConversations/${uid}/conversations/${convDoc.id}/messages`);
  }
  await wipeCollection(`wizardConversations/${uid}/conversations`);
  await wipeCollection(`wizardConversations/${uid}/contexts`);
  await wipeCollection(`aiInsights/${uid}/insights`);

  // Leave every organisation the user belongs to
  const membershipSnap = await getDoc(doc(db, "organisationMemberships", uid));
  if (membershipSnap.exists()) {
    const orgIds = Object.keys(membershipSnap.data()?.organisationIds || {});
    for (const orgId of orgIds) {
      await organisationService.removeMember(orgId, uid).catch(() => undefined);
    }
    await deleteDoc(doc(db, "organisationMemberships", uid)).catch(() => undefined);
  }

  // Username
  if (userData?.username) {
    await usernameService.releaseUsername(userData.username).catch(() => undefined);
  }

  // Profile images
  await deleteStorageObjectIfExists(`profile-images/${uid}`);
  await deleteStorageObjectIfExists(`profile-images-webp/${uid}/normal.webp`);
  await deleteStorageObjectIfExists(`profile-images-webp/${uid}/tiny.webp`);

  // Legal record stays — only mark it fulfilled, never delete this doc.
  await userService.markDeletionFulfilled(uid);

  // User data — last Firestore write before the Auth account goes.
  await userService.deleteUserAccount(uid);

  // Auth account — must be last: once this succeeds, the user is no longer
  // authenticated and can't perform any further writes as themselves.
  const currentUser = getAuth().currentUser;
  if (currentUser && currentUser.uid === uid) {
    await deleteUser(currentUser);
  }
}
